import React from "react";
import { Phone, Mail, MapPin, CalendarCheck } from "lucide-react";
import { COMPANY, waLink } from "@/lib/company";
import { WaIcon } from "@/lib/whatsapp";
import { usePageMeta } from "@/lib/seo";
import InquiryForm from "@/components/InquiryForm";
import LocationMap from "@/components/LocationMap";

const Card = ({ href, icon: Icon, label, value, sub, testId, external }) => {
  const Tag = href ? "a" : "div";
  return (
    <Tag href={href} data-testid={testId} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className="group rounded-xl border border-line bg-surface p-6 block transition-colors hover:border-silver/40">
      <Icon size={20} strokeWidth={1.6} className="mb-4 text-brand" />
      <p className="text-[10px] text-faint uppercase tracking-[0.25em] mb-1.5">{label}</p>
      <p className="font-semibold text-sm text-ink break-words">{value}</p>
      {sub && <p className="text-xs text-dim mt-1">{sub}</p>}
    </Tag>
  );
};

export default function Contact() {
  usePageMeta("Kontakt", `Kontakt zu ${COMPANY.legalName} – ${COMPANY.street}, ${COMPANY.city}. Telefon, WhatsApp und E-Mail.`);
  return (
    <div data-testid="contact-page" className="pt-28 md:pt-36 pb-24 max-w-7xl mx-auto px-5 sm:px-8">
      <p className="eyebrow">Kontakt</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-ink mt-5 mb-14 tracking-tight">Wir freuen uns auf Sie.</h1>

      <div className="grid lg:grid-cols-2 gap-10 mb-16">
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Card href={COMPANY.phoneLink} icon={Phone} label="Telefon" value={COMPANY.phone} testId="contact-phone-card" />
            <Card href={`mailto:${COMPANY.email}`} icon={Mail} label="E-Mail" value={COMPANY.email} testId="contact-email-card" />
            <Card href={waLink()} external icon={WaIcon} label="WhatsApp" value="Direkt schreiben" sub={COMPANY.phone} testId="contact-whatsapp-card" />
            <Card icon={MapPin} label="Verkaufsstandort" value={COMPANY.street} sub={`${COMPANY.city}, ${COMPANY.country}`} testId="contact-address-card" />
          </div>
          <div className="rounded-xl border border-line bg-surface p-7" data-testid="contact-hours-card">
            <p className="inline-flex items-center gap-2.5 font-display font-bold text-lg text-ink mb-3">
              <CalendarCheck size={18} className="text-brand" /> Öffnungszeiten
            </p>
            {COMPANY.hours ? (
              <div className="space-y-2.5">
                {COMPANY.hours.map(([day, time]) => (
                  <div key={day} className="flex justify-between text-sm"><span className="text-dim">{day}</span><span className="font-semibold text-ink">{time}</span></div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-dim leading-relaxed">
                Besichtigung und Probefahrt ausschließlich nach Terminvereinbarung – am schnellsten per WhatsApp oder Telefon.
              </p>
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-line bg-surface p-7 md:p-10">
          <p className="font-display font-bold text-xl text-ink mb-6">Nachricht senden</p>
          <InquiryForm type="kontakt" testId="contact" />
        </div>
      </div>

      <LocationMap testId="contact-map" />
    </div>
  );
}
